
import { useState, useEffect } from "react";
import { toast } from "@/hooks/use-toast";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Search, Building2, ArrowRight } from "lucide-react";
import { ContactFormValues } from "@/components/leads/add/schema";
import { createContact, updateLeadContact, LeadContact } from "@/services/leadContactsService";
import { fetchLeadsByName, fetchLeadById } from "@/services/api/leads";
import { LeadCompany } from "@/services/leadCompaniesService";
import { AddContactForm } from "@/components/contacts/AddContactForm";
import { cleanContactArrays } from "@/utils/contactDataHelpers";
import { useAuth } from "@/context/AuthContext";

export interface AddContactDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onContactAdded: () => void;
  departments?: string[];
  editContact?: LeadContact | null; // Optional contact to edit
  leadCompanyId?: string; // Optional for backward compatibility
}

export const AddContactDialog = ({
  open,
  onOpenChange,
  onContactAdded,
  departments = [],
  editContact = null,
  leadCompanyId,
}: AddContactDialogProps) => {
  const isEditMode = !!editContact;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [step, setStep] = useState<'search' | 'form'>('search');
  const [selectedLead, setSelectedLead] = useState<LeadCompany | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<LeadCompany[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isLoadingLead, setIsLoadingLead] = useState(false);
  const { user } = useAuth();
  // Reset state when dialog opens/closes
  useEffect(() => {
    if (open) {
      if (isEditMode && editContact) {
        // For edit mode, skip search and go directly to form
        setStep('form');
        
        // Try multiple sources for lead ID: contact's lead_company_id or passed leadCompanyId prop
        const leadIdToFetch = editContact.lead_company_id || leadCompanyId;
        
        if (leadIdToFetch) {
          console.log("🔍 Edit mode: Fetching lead data for ID:", leadIdToFetch);
          setIsLoadingLead(true);
          // Fetch actual lead data for edit mode
          fetchLeadById(leadIdToFetch.toString()).then((response) => {
            console.log("✅ Lead data fetched successfully:", response);
            const lead = response.success ? response.lead : response;
            setSelectedLead(lead);
          }).catch((error) => {
            console.error("❌ Failed to fetch lead data:", error);
            // Fallback to creating a basic lead object from contact data
            const leadCompany: LeadCompany = {
              id: leadIdToFetch.toString(),
              name: editContact.company?.name || editContact.company_name || 'Unknown Company',
              phone: null,
              industry: editContact.industry || '',
              lead_score: editContact.lead_score || 0,
              logo_url: null,
              company_size: null,
              website_url: '',
              linkedin_url: null,
              twitter_url: null,
              facebook_url: null,
              company_info: null,
              target_market: null,
              pain_points: null,
              ai_analysis: null,
              ai_insights: null,
              sentiment_score: null,
              engagement_score: null,
              last_analyzed: '',
              status: 'Active',
              created_at: '',
              updated_at: ''
            };
            console.log("🔄 Using fallback lead data:", leadCompany);
            setSelectedLead(leadCompany);
          }).finally(() => {
            setIsLoadingLead(false);
          });
        } else {
          console.log("⚠️ No lead ID found in editContact or leadCompanyId prop:", editContact);
          // Create a fallback lead from available contact data
          const fallbackLead: LeadCompany = {
            id: '',
            name: editContact.company?.name || editContact.company_name || 'Unknown Company',
            phone: null,
            industry: editContact.industry || 'Unknown',
            lead_score: editContact.lead_score || 0,
            logo_url: null,
            company_size: null,
            website_url: '',
            linkedin_url: null,
            twitter_url: null,
            facebook_url: null,
            company_info: null,
            target_market: null,
            pain_points: null,
            ai_analysis: null,
            ai_insights: null,
            sentiment_score: null,
            engagement_score: null,
            last_analyzed: '',
            status: 'Active',
            created_at: '',
            updated_at: ''
          };
          setSelectedLead(fallbackLead);
          setIsLoadingLead(false);
        }
      } else if (leadCompanyId) {
        // For add mode with leadCompanyId, fetch actual lead data and go directly to form
        console.log("🔍 Add mode: Fetching lead data for ID:", leadCompanyId);
        setStep('form');
        setIsLoadingLead(true);
        fetchLeadById(leadCompanyId).then((response) => {
          console.log("✅ Lead data fetched successfully for add mode:", response);
          // Handle different response formats from the API
          const lead = response.success ? response.lead : (response.lead || response);
          setSelectedLead(lead);
        }).catch((error) => {
          console.error("❌ Failed to fetch lead data for add mode:", error);
          setSelectedLead(null);
          toast.error("Failed to load lead information");
        }).finally(() => {
          setIsLoadingLead(false);
        });
      } else {
        // For add mode without leadCompanyId, start with search
        setStep('search');
        setSelectedLead(null);
        setIsLoadingLead(false);
      }
      setSearchTerm('');
      setSearchResults([]);
    }
  }, [open, isEditMode, editContact, leadCompanyId]);

  const handleSearchLeads = async (term: string) => {
    if (term.length < 2) {
      setSearchResults([]);
      return;
    }

    setIsSearching(true);
    try {
      const results = await fetchLeadsByName(term);
      setSearchResults(results || []);
    } catch (error) {
      console.error('Error searching leads:', error);
      toast.error('Failed to search leads');
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  const handleLeadSelect = (lead: LeadCompany) => {
    setSelectedLead(lead);
    setStep('form');
  };

  const handleBackToSearch = () => {
    setStep('search');
    setSelectedLead(null);
  };

  const handleSubmit = async (values: ContactFormValues) => {
    if (!isEditMode && !selectedLead && !leadCompanyId) {
      toast.error("Please select a lead first");
      return;
    }

    setIsSubmitting(true);
    try {
      if (isEditMode && editContact) {
        // Update existing contact
        console.log("🔄 AddContactDialog: Updating contact with data:", values);
        const cleanedData = cleanContactArrays(values);
        
        // Map to backend format - backend expects phones/emails arrays in same format as our form
        const updateData = { 
          ...values, 
          phones: cleanedData.phone, // Backend expects same format as our phone array
          emails: cleanedData.email, // Backend expects same format as our email array
          // Keep the original for any legacy compatibility
          phone: cleanedData.phone,
          email: cleanedData.email
        };
        
        console.log("🔄 AddContactDialog: Final update data being sent:", updateData);
        await updateLeadContact(editContact.id.toString(), updateData as any);
        console.log("✅ AddContactDialog: Contact updated successfully");
        toast.success("Contact updated successfully");
      } else {
        // Create new contact
        console.log("Adding contact with data:", values);
        
        // Use selectedLead or fallback to leadCompanyId
        const targetLeadId = selectedLead?.id || leadCompanyId;
        
        // Clean the contact arrays to ensure required properties
        const cleanedData = cleanContactArrays(values);
        
        // Prepare contact data for API - match exactly what backend expects
        const contactData = {
          lead_id: parseInt(targetLeadId!.toString()),
          first_name: values.first_name,
          last_name: values.last_name,
          name: `${values.first_name} ${values.last_name}`, // Keep for TypeScript interface
          email: cleanedData.email,
          phone: cleanedData.phone,
          phones: cleanedData.phone, // Backend expects same format as our phone array  
          emails: cleanedData.email, // Backend expects same format as our email array
          designation: values.designation || "",
          department: values.department || "",
          urls: values.urls || {},
          is_primary: values.decision_maker || false,
          decision_maker: values.decision_maker || false,
          contact_status: values.status || "Active",
          notes: values.notes || "",
          metadata: values.metadata || {},
          created_by: user?.id || null,
          lead_company_id: targetLeadId!.toString()
        };
        
        // Call the actual API to create contact
        const response = await createContact(contactData as any);
        console.log("Contact added successfully:", response);
        toast.success("Contact added successfully");
      }
      
      console.log("🎉 AddContactDialog: Contact operation successful, calling onContactAdded");
      // Only call onContactAdded - let the parent handle closing
      onContactAdded();
      console.log("✅ AddContactDialog: onContactAdded called successfully");
    } catch (error) {
      console.error(`Error ${isEditMode ? 'updating' : 'adding'} contact:`, error);
      toast.error(`Failed to ${isEditMode ? 'update' : 'add'} contact. Please try again.`);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[700px] h-[650px] p-0 flex flex-col">
        <DialogHeader className="px-4 pt-3 pb-1 border-b">
          <DialogTitle>
            {step === 'search' 
              ? (isEditMode ? 'Change Lead' : 'Select Lead') 
              : (isEditMode ? 'Edit Contact' : 'Add New Contact')
            }
          </DialogTitle>
          <DialogDescription className="text-sm">
            {step === 'search' 
              ? (isEditMode 
                  ? 'Search for a different lead for this contact.' 
                  : 'Search for a lead to add the contact to.'
                ) 
              : (isEditMode 
                  ? `Editing ${editContact?.first_name} ${editContact?.last_name}` 
                  : `Adding contact for ${selectedLead?.name || 'Selected Lead'}`
                )
            }
          </DialogDescription>
        </DialogHeader>
        
        <div className="flex-1 overflow-hidden">
          {step === 'search' ? (
            <div className="px-4 py-3 h-full overflow-y-auto scrollbar-custom">
              <div className="space-y-4">
                <div className="space-y-2">
                  <div className="relative">
                    <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="search"
                      placeholder="Type lead name to search..."
                      value={searchTerm}
                      onChange={(e) => {
                        setSearchTerm(e.target.value);
                        handleSearchLeads(e.target.value);
                      }}
                      className="pl-10"
                    />
                  </div>
                </div>

                {isSearching && (
                  <div className="text-center py-4">
                    <p className="text-muted-foreground">Searching leads...</p>
                  </div>
                )}

                {searchResults.length > 0 && (
                  <div className="space-y-2 max-h-60 overflow-y-auto scrollbar-custom">
                    {searchResults.map((lead) => (
                      <Card key={lead.id} className="cursor-pointer hover:bg-muted/50 transition-colors">
                        <CardContent className="p-4" onClick={() => handleLeadSelect(lead)}>
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-3">
                              <Building2 className="h-5 w-5 text-muted-foreground" />
                              <div>
                                <h4 className="font-medium">{lead.name}</h4>
                                <p className="text-sm text-muted-foreground">
                                  {lead.industry} • Score: {lead.lead_score || 'N/A'}
                                </p>
                              </div>
                            </div>
                            <ArrowRight className="h-4 w-4 text-muted-foreground" />
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}

                {searchTerm.length >= 2 && !isSearching && searchResults.length === 0 && (
                  <div className="text-center py-4">
                    <p className="text-muted-foreground">No leads found matching "{searchTerm}"</p>
                  </div>
                )}

                <div className="flex justify-end pt-4">
                  <Button variant="outline" onClick={() => onOpenChange(false)}>
                    Cancel
                  </Button>
                </div>
              </div>
            </div>
          ) : (
            <>
              {isLoadingLead ? (
                <div className="flex items-center justify-center h-full">
                  <p className="text-muted-foreground">Loading lead information...</p>
                </div>
              ) : (
                <AddContactForm
                  onSubmit={handleSubmit}
                  isSubmitting={isSubmitting}
                  onCancel={() => onOpenChange(false)}
                  departments={departments}
                  editContact={editContact}
                  selectedLead={selectedLead}
                  onChangeLead={handleBackToSearch}
                />
              )}
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AddContactDialog;
